/**
 * The browser-storage keys the legacy manager uses, kept byte-identical.
 *
 * They are not an implementation detail: a staff member mid-shift when the new
 * manager is deployed keeps their session only if the new app reads the key the
 * old one wrote. The `token` cookie carries the same session, so either path
 * authenticates.
 */
export const STORAGE_KEYS = {
  /** Staff JWT. Also written by the legacy app. */
  token: 'fk_token',
  /** Cached `AuthUser`, so the shell renders before `/auth/me` answers. */
  user: 'fk_user',
  /** Active franchise for the switcher — sent as `X-Franchise-Id`. */
  franchiseId: 'fk_franchise_id',
  /** Control Tower master key; when present it outranks `fk_token`. */
  franchiseMasterKey: 'fk_franchise_master_key',
  /** Control Tower jump target: an absolute API base for another franchise. */
  franchiseApiBaseUrl: 'fk_franchise_url',
  /** UI language chosen in the profile (never inferred from the browser). */
  language: 'fk_lang',
  /** Stable per-device id used by the kasa surfaces. */
  deviceId: 'kasa_device_id',
  /**
   * Customer JWT, for the customer ordering app.
   *
   * Deliberately **not** `fk_token`. The two sessions are different people with different rights, and
   * a manager who opens the customer site in the same browser must not have their staff session
   * overwritten by signing in as a customer — or the other way round. Legacy never had this problem
   * because it attached the customer header by hand at each call site; AGOS routes every request
   * through `customFetch`, so the token needs somewhere to live that the staff key does not own.
   */
  customerToken: 'fk_customer_token',
  /**
   * Control Tower JWT — legacy's own key, kept byte-identical for the same reason as `fk_token`:
   * a super-admin mid-session when the new tower is deployed keeps it.
   *
   * It is **RS256 and platform-issued**. `TowerJwtSigner` says why that matters: *"a franchise's
   * api-server holds only the public key, so it can verify that the platform vouched for somebody but
   * cannot mint a token that says so. Nothing running inside a tenant can promote itself."* Nothing in
   * this repository mints one; the platform does, from an ordinary login.
   */
  towerToken: 'hk_super_admin_token',
  /**
   * Partner portal JWT — a **third principal**, with its own secret server-side.
   *
   * Legacy keeps it apart deliberately and renders the portal before the super-admin context mounts, so
   * a partner's session never triggers a super-admin probe. Separate key, separate app.
   */
  partnerToken: 'hk_partner_token',
} as const

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]

function readLocalStorage(key: StorageKey): string | null {
  // Private-mode Safari and locked-down kiosk profiles throw on access rather
  // than returning null, and a throw here would take down every request.
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * The bearer token to send. The Control Tower master key wins when set — that
 * is what lets a super-admin inspect a franchise without holding its staff
 * token (BACKEND.md §5, resolution priority 1).
 *
 * The customer token is last, and only ever answers on an origin where no staff
 * session exists — the two apps are served from different hosts. Ordering it
 * behind the staff token means that if both somehow exist, the staff one wins,
 * which is the safer way round: a customer token on a manager screen would fail
 * every authorised call rather than quietly reading less than it should.
 */
export function getAuthToken(): string | null {
  return (
    readLocalStorage(STORAGE_KEYS.franchiseMasterKey) ??
    readLocalStorage(STORAGE_KEYS.token) ??
    readLocalStorage(STORAGE_KEYS.customerToken) ??
    readLocalStorage(STORAGE_KEYS.towerToken) ??
    readLocalStorage(STORAGE_KEYS.partnerToken)
  )
}

function writeLocalStorage(key: StorageKey, token: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return
    if (token === null) localStorage.removeItem(key)
    else localStorage.setItem(key, token)
  } catch {
    // A locked-down profile throws; losing the token is better than taking the app down on sign-in.
  }
}

/** Written by the Control Tower on sign-in, cleared on sign-out. */
export function setTowerAuthToken(token: string | null): void {
  writeLocalStorage(STORAGE_KEYS.towerToken, token)
}

/** Written by the partner portal, which is a different person from the super-admin. */
export function setPartnerAuthToken(token: string | null): void {
  writeLocalStorage(STORAGE_KEYS.partnerToken, token)
}

/** Written by the customer app on sign-in, cleared on sign-out. */
export function setCustomerAuthToken(token: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return
    if (token === null) localStorage.removeItem(STORAGE_KEYS.customerToken)
    else localStorage.setItem(STORAGE_KEYS.customerToken, token)
  } catch {
    // Same reason as `readLocalStorage`: a locked-down profile throws, and losing the token is better
    // than taking the app down on sign-in.
  }
}

export function getActiveFranchiseId(): string | null {
  return readLocalStorage(STORAGE_KEYS.franchiseId)
}

/**
 * A Control Tower jump can point the client at another franchise's API host.
 * Legacy supports it through this key and the manager-platform port has to
 * decide whether AGOS keeps it or resolves the same case through
 * `X-Franchise-Id` alone — see docs/INVENTORY/manager-open-questions.md.
 */
export function getApiBaseUrlOverride(): string | null {
  const stored = readLocalStorage(STORAGE_KEYS.franchiseApiBaseUrl)
  return stored ? stored.replace(/\/+$/, '') : null
}
